/**
 * Estructura unificada de error de la API (ApiErrorResponse).
 * La API devuelve: { message, errors?, detail? }
 */
export interface ApiErrorResponse {
  message: string;
  errors?: Record<string, string[]>;
  detail?: string;
}

function joinApiErrorMessages(messages: string[]): string {
  const unique = Array.from(new Set(messages.map((m) => m.trim()).filter(Boolean)));
  if (unique.length === 0) return '';
  if (unique.length === 1) return unique[0];
  return unique.join('\n');
}

function collectErrorsFromRecord(errors: Record<string, unknown>): string[] {
  const errorMessages: string[] = [];
  for (const key of Object.keys(errors)) {
    const value = errors[key];
    if (Array.isArray(value)) {
      errorMessages.push(...value.filter((v): v is string => typeof v === 'string'));
    } else if (typeof value === 'string') {
      errorMessages.push(value);
    }
  }
  return errorMessages;
}

/**
 * Extrae el mensaje de error de una respuesta de la API para mostrarlo al usuario.
 * Soporta la estructura unificada { message, errors?, detail? } y formatos legacy:
 * - JSON con { message } o { mensaje }
 * - Cuerpo en texto plano (string)
 * - ModelState de ASP.NET Core con { message, errors }
 */
export function getApiErrorMessage(
  error: unknown,
  defaultMessage: string = 'Ha ocurrido un error'
): string {
  if (error == null) return defaultMessage;

  const data = (error as { response?: { data?: unknown } })?.response?.data;

  if (typeof data === 'string' && data.trim()) {
    return data.trim();
  }

  if (data && typeof data === 'object') {
    const obj = data as Record<string, unknown>;

    // Priorizar errors.Items u otros errores de validación antes del message genérico.
    if (obj.errors && typeof obj.errors === 'object' && !Array.isArray(obj.errors)) {
      const errorMessages = collectErrorsFromRecord(obj.errors as Record<string, unknown>);
      if (errorMessages.length > 0) {
        return joinApiErrorMessages(errorMessages);
      }
    }

    const possibleModelStateErrors: string[] = [];
    for (const key in obj) {
      const value = obj[key];
      if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'string') {
        possibleModelStateErrors.push(...(value as string[]));
      }
    }
    if (possibleModelStateErrors.length > 0) {
      return joinApiErrorMessages(possibleModelStateErrors);
    }

    const msg =
      obj.message ??
      obj.mensaje ??
      obj.Message ??
      obj.Mensaje ??
      obj.detail ??
      obj.error ??
      obj.title;
    if (msg != null && typeof msg === 'string' && msg.trim()) {
      return msg.trim();
    }
    if (Array.isArray(obj.errors) && obj.errors.length > 0) {
      const first = obj.errors[0];
      if (typeof first === 'string') return first;
      if (first && typeof first === 'object' && 'message' in first) {
        return String((first as { message?: string }).message ?? defaultMessage);
      }
    }
  }

  const message = (error as Error)?.message;
  if (typeof message === 'string' && message.trim()) {
    return message.trim();
  }

  return defaultMessage;
}
